import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { RecordWebVitalBodyDto } from '@malou-io/package-dto';
import { WebVitalMetricName, WebVitalNavigationType, WebVitalRating } from '@malou-io/package-utils';

import { environment } from '#environments/environment';

interface WebVitalsMetric {
    name: 'CLS' | 'FCP' | 'INP' | 'LCP' | 'TTFB';
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    id: string;
    navigationType?: 'navigate' | 'reload' | 'back-forward' | 'back-forward-cache' | 'prerender' | 'restore';
}

/**
 * Service for measuring and reporting Core Web Vitals metrics.
 *
 * Metrics tracked:
 * - CLS (Cumulative Layout Shift): Visual stability
 * - FCP (First Contentful Paint): Perceived load speed
 * - INP (Interaction to Next Paint): Responsiveness (replaces FID)
 * - LCP (Largest Contentful Paint): Loading performance
 * - TTFB (Time to First Byte): Server response time
 */
@Injectable({
    providedIn: 'root',
})
export class WebVitalsService {
    readonly API_BASE_URL = `${environment.APP_MALOU_API_URL}/api/v1/web-vitals`;

    private readonly _router = inject(Router);

    private _isInitialized = false;
    private _pendingMetrics: RecordWebVitalBodyDto[] = [];
    private _flushTimeout: ReturnType<typeof setTimeout> | null = null;

    private readonly _flushIntervalMs = 12000;
    private readonly _maxBatchSize = 20;

    /**
     * Initialize web vitals tracking.
     * Should be called once during application bootstrap.
     */
    initialize(): void {
        if (this._isInitialized) {
            return;
        }

        this._isInitialized = true;

        window.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this._flushMetrics();
            }
        });

        // Dynamic import of web-vitals library, to avoid blocking the main thread at bootstrap
        import('web-vitals')
            .then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
                onCLS((metric) => this._reportMetric(metric));
                onFCP((metric) => this._reportMetric(metric));
                onINP((metric) => this._reportMetric(metric));
                onLCP((metric) => this._reportMetric(metric));
                onTTFB((metric) => this._reportMetric(metric));
            })
            .catch((err) => {
                console.warn('[WebVitalsService] Failed to load web-vitals library', err);
            });
    }

    private _reportMetric(metric: WebVitalsMetric): void {
        const { name, value, rating, id, navigationType } = metric;
        const route = this._getCurrentRoute();

        if (
            !this._isValidWebVitalName(name) ||
            !this._isValidWebVitalRating(rating) ||
            !this._isValidWebVitalNavigationType(navigationType)
        ) {
            return;
        }

        this._queueMetric({
            name,
            value,
            rating,
            id,
            navigationType,
            route,
            userAgent: navigator.userAgent,
        });

        if (!environment.production) {
            console.info(`[WebVitals] ${name}:`, {
                value: name === 'CLS' ? value.toFixed(3) : `${Math.round(value)}ms`,
                rating,
                route,
            });
        }
    }

    private _queueMetric(metric: RecordWebVitalBodyDto): void {
        this._pendingMetrics.push(metric);

        const isBatchFull = this._pendingMetrics.length >= this._maxBatchSize;
        if (isBatchFull) {
            this._flushMetrics();
            return;
        }

        // Schedule flush if not already scheduled
        if (!this._flushTimeout) {
            this._flushTimeout = setTimeout(() => this._flushMetrics(), this._flushIntervalMs);
        }
    }

    private _flushMetrics(): void {
        if (this._flushTimeout) {
            clearTimeout(this._flushTimeout);
            this._flushTimeout = null;
        }

        if (this._pendingMetrics.length === 0) {
            return;
        }

        const metricsToSend = [...this._pendingMetrics];
        this._pendingMetrics = [];

        const body = JSON.stringify({ metrics: metricsToSend });

        fetch(this.API_BASE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
        }).catch((err) => console.warn('[WebVitalsService] Failed keepalive fetch', err));
    }

    private _getCurrentRoute(): string {
        // Get the route pattern (e.g., '/restaurants/:id/reviews') instead of actual URL
        const urlTree = this._router.parseUrl(this._router.url);
        let currentRoute = urlTree.root.children.primary?.segments.map((s) => s.path).join('/') || '/';

        if (currentRoute === '/') {
            const pathname = window.location.pathname;
            currentRoute = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
        }

        return this._sanitizeRoute(currentRoute);
    }

    private _sanitizeRoute(route: string): string {
        if (!route || route === '/') {
            return '/';
        }

        const sanitizedRoute = route
            // replace all MongoDB ObjectIDs (ex: 65719e39827a77655ea28f2d) by :id
            // this is critical to prevent having a cardinality explosion in Grafana
            .replace(/\b[a-fA-F0-9]{24}\b/g, ':id')
            // auth/password/new/:id/eyJhb.eyJ1c2VyS.Co7MKX9Wd -> auth/password/new/:id/:token
            // auth/confirm/:id/eyJhb.eyJ1c2VyS.Co7MKX9Wd -> auth/confirm/:id/:token
            .replace(/auth\/(password\/new|confirm)\/:id\/[a-zA-Z0-9._-]+.*/, 'auth/$1/:id/:token');

        return sanitizedRoute;
    }

    private _isValidWebVitalName(name: string): name is WebVitalMetricName {
        return Object.values(WebVitalMetricName).includes(name as WebVitalMetricName);
    }

    private _isValidWebVitalRating(rating: string): rating is WebVitalRating {
        return Object.values(WebVitalRating).includes(rating as WebVitalRating);
    }

    private _isValidWebVitalNavigationType(navigationType?: string): navigationType is WebVitalNavigationType | undefined {
        return !navigationType || Object.values(WebVitalNavigationType).includes(navigationType as WebVitalNavigationType);
    }
}
